import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface BulkActionBarProps {
  count: number;
  onClear: () => void;
  children: ReactNode;
  className?: string;
}

/**
 * Sticky bar that appears when items are selected.
 * - Slides down with framer-motion.
 * - Rests at top of the page area (z-index 30 — under modals z-50).
 */
export function BulkActionBar({
  count,
  onClear,
  children,
  className,
}: BulkActionBarProps): JSX.Element {
  return (
    <AnimatePresence>
      {count > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className={cn(
            'sticky top-0 z-30 flex flex-wrap items-center gap-2 rounded-lg border border-primary/40 bg-primary px-3 py-2 text-primary-foreground shadow-lg',
            className,
          )}
          role="toolbar"
          aria-label="Actions en masse"
        >
          <button
            type="button"
            onClick={onClear}
            aria-label="Désélectionner"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-primary-foreground/15"
          >
            <X className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium">
            {count} élément{count > 1 ? 's' : ''} sélectionné{count > 1 ? 's' : ''}
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-1">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface BulkButtonProps {
  onClick: () => void;
  disabled?: boolean;
  variant?: 'default' | 'destructive';
  children: ReactNode;
}

export function BulkButton({
  onClick,
  disabled,
  variant = 'default',
  children,
}: BulkButtonProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-colors',
        variant === 'destructive'
          ? 'bg-destructive/90 text-destructive-foreground hover:bg-destructive'
          : 'bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/25',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {children}
    </button>
  );
}
